import { useIntl } from "react-intl";

import { GetAgileArticleTitleDescription } from "@/contentful/graphql/agile-article";
import { GetAgileOverviewTitleDescription } from "@/contentful/graphql/agile-overview";
import { GetAgileTutorialTitleDescription } from "@/contentful/graphql/agile-tutorial";

import {
    GetConfluenceTemplateCollectionDetailsTitleDescription,
    GetConfluenceTemplateCategoryTitleDescription
} from "../page-wrapper";

import type { PreloadedQuery } from "react-relay";
import type { OperationType } from "relay-runtime";

interface DynamicTitleDescriptionResult {
    title: string | null;
    description: string | null;
}

// Extract title/description for agile routes
function getAgileTitleDescription(
    routePath: string,
    queryRef: PreloadedQuery<OperationType>,
    intl: ReturnType<typeof useIntl>
): DynamicTitleDescriptionResult {
    const result: DynamicTitleDescriptionResult = {
        title: null,
        description: null
    };

    if (
        /(agile\/(scrum|kanban|project-management|product-management|agile-at-scale|software-development|design|agile-marketing|advantage|teams|tutorials|agile-conversations))$/.test(
            routePath
        )
    ) {
        const response = GetAgileOverviewTitleDescription(queryRef);
        if (response?.title) {
            result.title = intl.formatMessage({
                id: "AgileOverviewPage.title",
                defaultMessage: `${response.title} | Atlassian`
            });
            result.description = response.description ?? null;
        }
    } else if (routePath.startsWith("/agile/tutorials/")) {
        const response = GetAgileTutorialTitleDescription(queryRef);
        if (response?.title) {
            result.title = `${response.title} | Atlassian`;
            result.description = response.description ?? null;
        }
    } else if (routePath.startsWith("/agile")) {
        const response = GetAgileArticleTitleDescription(queryRef);
        if (response?.title) {
            result.title = intl.formatMessage({
                id: "AgileArticlePage.title",
                defaultMessage: `${response.title} | Atlassian`
            });
            result.description = response.description ?? null;
        }
    }

    return result;
}

// Extract title/description for Confluence template routes
function getConfluenceTemplateTitleDescription(
    routePath: string,
    queryRef: PreloadedQuery<OperationType> | undefined,
    intl: ReturnType<typeof useIntl>
): DynamicTitleDescriptionResult {
    const result: DynamicTitleDescriptionResult = {
        title: null,
        description: null
    };

    if (routePath.startsWith("/software/confluence/templates/collections/")) {
        const urlSlug = routePath.split("/").pop() ?? "";
        const response = GetConfluenceTemplateCollectionDetailsTitleDescription(
            urlSlug,
            queryRef
        );
        if (response?.title) {
            let title = response.title;

            // Apply title formatting for English locale
            if (intl.locale === "en") {
                title = intl.formatMessage(
                    {
                        id: "ConfluenceTemplatesCollectionDetailPage.pageTitle",
                        defaultMessage: "Free {title} collection | Confluence"
                    },
                    { title }
                );
            } else {
                title +=
                    " " +
                    intl.formatMessage({
                        id: "ConfluenceTemplatesCollectionDetailPage.title-suffix",
                        defaultMessage: "Collection | Atlassian"
                    });
            }

            result.title = title;
            result.description = response.description;
        }
    } else if (
        routePath.startsWith("/software/confluence/templates/categories/")
    ) {
        const urlSlug = routePath.split("/").pop() ?? "";
        const response = GetConfluenceTemplateCategoryTitleDescription(
            urlSlug,
            queryRef
        );
        if (response?.title) {
            let title = response.title;

            // Apply title formatting for English locale
            if (intl.locale === "en") {
                title = intl.formatMessage(
                    {
                        id: "ConfluenceTemplatesCategoryDetailPage.pageTitle",
                        defaultMessage: "Free {title} templates | Confluence"
                    },
                    { title }
                );
            } else {
                title += " | Atlassian";
            }

            result.title = title;
            result.description = response.description;
        }
    }

    return result;
}

// Update DOM with title and description
function updateDocumentMetadata(
    title: string | null,
    description: string | null
): void {
    if (title && title !== document.title) {
        document.title = title;
    }
    if (description) {
        const metaDescription = document.querySelector(
            'meta[name="description"]'
        );
        if (metaDescription) {
            metaDescription.setAttribute("content", description);
        } else {
            // Create meta description if it doesn't exist
            const meta = document.createElement("meta");
            meta.name = "description";
            meta.content = description;
            document.head.appendChild(meta);
        }
    }
}

export function useDynamicTitleDescription(
    location: Location,
    queryRef: PreloadedQuery<OperationType> | undefined
) {
    try {
        // Must call hooks unconditionally
        const intl = useIntl();

        // Only run on client side and when we have queryRef
        if (typeof window === "undefined" || !queryRef) {
            return;
        }

        const routePath = location.pathname;

        let result: DynamicTitleDescriptionResult = {
            title: null,
            description: null
        };

        // Handle agile routes
        if (routePath.startsWith("/agile")) {
            result = getAgileTitleDescription(routePath, queryRef, intl);
        }
        // Handle Confluence template routes
        else if (routePath.startsWith("/software/confluence/templates/")) {
            result = getConfluenceTemplateTitleDescription(
                routePath,
                queryRef,
                intl
            );
        }

        // Update document metadata
        updateDocumentMetadata(result.title, result.description);
    } catch (error) {
        // Silently handle any errors in title/description extraction
        console.warn("Error updating dynamic title/description:", error);
    }
}
